import React, { useState } from 'react';
import { Building2Icon, CalendarDaysIcon, CheckCircle2Icon, Globe2Icon, MapPinIcon, PlusIcon, SaveIcon, Trash2Icon, UserRoundIcon, UsersRoundIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Eyebrow } from '../components/ui/Reveal';
type FirmProfile = {
  name: string;
  website: string;
  industry: string;
  size: string;
  headquarters: string;
  founded: string;
  description: string;
  founderName: string;
  founderTitle: string;
  founderEmail: string;
  founderLinkedin: string;
};
type TeamMember = {
  id: string;
  name: string;
  title: string;
  email: string;
  department: string;
};
const profileStorageKey = 'ardent-firm-profile';
const teamStorageKey = 'ardent-firm-team';
const initialProfile: FirmProfile = {
  name: 'Northwind Capital',
  website: 'northwindcapital.com',
  industry: 'Private Equity',
  size: '51–200 employees',
  headquarters: 'New York, United States',
  founded: '2012',
  description: 'Northwind Capital partners with exceptional management teams to build enduring mid-market businesses.',
  founderName: 'Marcus Devlin',
  founderTitle: 'Founder & Managing Partner',
  founderEmail: 'marcus@northwindcapital.com',
  founderLinkedin: 'linkedin.com/in/marcus-devlin'
};
const initialTeam: TeamMember[] = [{
  id: '1',
  name: 'Clara Hastings',
  title: 'Chief People Officer',
  email: 'clara@northwindcapital.com',
  department: 'People & Culture'
}, {
  id: '2',
  name: 'Daniel Lee',
  title: 'Operating Partner',
  email: 'daniel@northwindcapital.com',
  department: 'Portfolio Operations'
}];
export function Firm() {
  const [profile, setProfile] = useState<FirmProfile>(() => readStored(profileStorageKey, initialProfile));
  const [team, setTeam] = useState<TeamMember[]>(() => readStored(teamStorageKey, initialTeam));
  const [saved, setSaved] = useState(false);
  const updateProfile = (key: keyof FirmProfile, value: string) => {
    setProfile(current => ({
      ...current,
      [key]: value
    }));
    setSaved(false);
  };
  const updateMember = (id: string, key: keyof Omit<TeamMember, 'id'>, value: string) => {
    setTeam(members => members.map(member => member.id === id ? {
      ...member,
      [key]: value
    } : member));
    setSaved(false);
  };
  const addMember = () => {
    setTeam(members => [...members, {
      id: createId(),
      name: '',
      title: '',
      email: '',
      department: ''
    }]);
    setSaved(false);
  };
  const removeMember = (id: string) => {
    setTeam(members => members.filter(member => member.id !== id));
    setSaved(false);
  };
  const saveFirm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    window.localStorage.setItem(profileStorageKey, JSON.stringify(profile));
    window.localStorage.setItem(teamStorageKey, JSON.stringify(team));
    setSaved(true);
  };
  return <div className="mx-auto max-w-6xl px-6 py-12 lg:px-10 lg:py-16" data-id="element-395" data-mp="21i617duhk4p5">
      <div className="flex flex-col justify-between gap-6 border-b border-ink/10 pb-10 sm:flex-row sm:items-end" data-id="element-396" data-mp="gzcb0zkvpao7">
        <div className="max-w-2xl" data-id="element-397" data-mp="lrvlvos6lrpb">
          <Eyebrow data-id="element-398" data-mp="lsyh28wjegb1">Employer workspace</Eyebrow>
          <h1 className="mt-6 font-serif text-4xl leading-tight text-ink sm:text-5xl" data-id="element-399" data-mp="80qj4dtiojar">
            Your firm
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-ink-600" data-id="element-400" data-mp="c1k87aip4458">
            Keep the organisation, founder and employer details behind your searches current
            and consistent.
          </p>
        </div>
        {saved && <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-2 text-sm font-medium text-emerald-700" data-id="element-401" data-mp="qq6vpzih827j">
            <CheckCircle2Icon className="h-4 w-4" data-id="element-402" data-mp="vo8j2wmxqnot" /> Changes saved
          </span>}
      </div>

      <form onSubmit={saveFirm} className="mt-10 space-y-8" data-id="element-403" data-mp="3bfmkkoy4xguj">
        <section className="overflow-hidden rounded-3xl border border-ink/10 bg-ivory-50 shadow-soft" data-id="element-404" data-mp="3gf8yz1pp934x">
          <div className="flex flex-col gap-4 border-b border-ink/10 bg-ink px-7 py-6 text-ivory-100 sm:flex-row sm:items-center sm:justify-between" data-id="element-405" data-mp="3vovx6tzafvtk">
            <div className="flex items-center gap-3" data-id="element-406" data-mp="3qx4dn3ejuqcx">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold text-ink" data-id="element-407" data-mp="3lyuigsstrprb">
                <Building2Icon className="h-5 w-5" data-id="element-408" data-mp="3hyk16jzl1cx5" />
              </span>
              <div data-id="element-409" data-mp="iinbodf45ceb">
                <h2 className="font-serif text-xl" data-id="element-410" data-mp="ij5g3xbxiwxb">Firm details</h2>
                <p className="mt-0.5 text-sm text-ink-400" data-id="element-411" data-mp="ig9kifnk0kc1">The profile your search partners see.</p>
              </div>
            </div>
            <span className="text-xs uppercase tracking-widest2 text-gold" data-id="element-412" data-mp="9h5zh64ttuuw">Organisation</span>
          </div>

          <div className="grid gap-5 p-7 sm:grid-cols-2" data-id="element-413" data-mp="4ht4tz7prjhr">
            <TextField label="Firm name" value={profile.name} onChange={value => updateProfile('name', value)} required data-id="element-414" data-mp="2u86j9mapouga" />
            <TextField label="Website" value={profile.website} onChange={value => updateProfile('website', value)} prefix={<Globe2Icon className="h-4 w-4" data-id="element-416" data-mp="19qnh9zi2gzlc" />} type="url" data-id="element-415" data-mp="2pbg02q3m1iqn" />
            <TextField label="Industry" value={profile.industry} onChange={value => updateProfile('industry', value)} data-id="element-417" data-mp="1dqxpzjx8u43w" />
            <SelectField label="Firm size" value={profile.size} onChange={value => updateProfile('size', value)} options={['1–10 employees', '11–50 employees', '51–200 employees', '201–500 employees', '501–1,000 employees', '1,000+ employees']} data-id="element-418" data-mp="1nibvq9fsg05h" />
            <TextField label="Headquarters" value={profile.headquarters} onChange={value => updateProfile('headquarters', value)} prefix={<MapPinIcon className="h-4 w-4" data-id="element-420" data-mp="72iqpnlz9ycc" />} data-id="element-419" data-mp="21g0g7g5y15a0" />
            <TextField label="Founded" value={profile.founded} onChange={value => updateProfile('founded', value)} prefix={<CalendarDaysIcon className="h-4 w-4" data-id="element-422" data-mp="15go9l5yw26t8" />} inputMode="numeric" data-id="element-421" data-mp="753skcma0ea3" />
            <label className="block sm:col-span-2" data-id="element-423" data-mp="1jgfqpfkpc3bg">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-ink-600" data-id="element-424" data-mp="1fcrn17z6xrvh">
                About your firm
              </span>
              <textarea rows={4} value={profile.description} onChange={event => updateProfile('description', event.target.value)} className="w-full resize-none rounded-xl border border-ink/15 bg-ivory-100 px-4 py-3 text-sm leading-relaxed text-ink placeholder:text-ink-400 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold" placeholder="Describe your firm, its mission and what makes it distinctive…" data-id="element-425" data-mp="1tazdqy5mvwy7" />
            </label>
          </div>
        </section>

        <section className="rounded-3xl border border-ink/10 bg-ivory-50 p-7 shadow-soft" data-id="element-426" data-mp="2qraa61y6o1gm">
          <div className="flex items-start gap-3" data-id="element-427" data-mp="2voajclqbxkby">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold/15 text-gold-dark" data-id="element-428" data-mp="2vmzesjp288js">
              <UserRoundIcon className="h-5 w-5" data-id="element-429" data-mp="2lqw42pg571l8" />
            </span>
            <div data-id="element-430" data-mp="2lt8u7pw20i9d">
              <h2 className="font-serif text-xl text-ink" data-id="element-431" data-mp="27zrc89j0ndlg">Founder / primary leader</h2>
              <p className="mt-1 text-sm text-ink-600" data-id="element-432" data-mp="27xy1ai1de77n">
                Add the person who leads the firm or is your main point of contact.
              </p>
            </div>
          </div>

          <div className="mt-7 grid gap-5 sm:grid-cols-2" data-id="element-433" data-mp="1xzicwmtq9c1u">
            <TextField label="Full name" value={profile.founderName} onChange={value => updateProfile('founderName', value)} required data-id="element-434" data-mp="231przl9ut88e" />
            <TextField label="Title" value={profile.founderTitle} onChange={value => updateProfile('founderTitle', value)} data-id="element-435" data-mp="1tyoo7r8gnt7w" />
            <TextField label="Work email" value={profile.founderEmail} onChange={value => updateProfile('founderEmail', value)} type="email" data-id="element-436" data-mp="24in3f6w3aryv" />
            <TextField label="LinkedIn profile" value={profile.founderLinkedin} onChange={value => updateProfile('founderLinkedin', value)} data-id="element-437" data-mp="1vhgimlmequzt" />
          </div>
        </section>

        <section className="rounded-3xl border border-ink/10 bg-ivory-50 p-7 shadow-soft" data-id="element-438" data-mp="1lqc39kstl84g">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start" data-id="element-439" data-mp="1gtlnyzouagk3">
            <div className="flex items-start gap-3" data-id="element-440" data-mp="1bvtva3gdeqeu">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ink text-gold" data-id="element-441" data-mp="17t5oe1b7vtki">
                <UsersRoundIcon className="h-5 w-5" data-id="element-442" data-mp="12tb085itti35" />
              </span>
              <div data-id="element-443" data-mp="3eh6bcj9l9uh0">
                <h2 className="font-serif text-xl text-ink" data-id="element-444" data-mp="3ehph71lysseo">People at your firm</h2>
                <p className="mt-1 text-sm text-ink-600" data-id="element-445" data-mp="3jfhmcxbmeb0t">
                  Add the employers and colleagues involved in your hiring process.
                </p>
              </div>
            </div>
            <button type="button" onClick={addMember} className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:border-gold hover:bg-gold/10" data-id="element-446" data-mp="3sgfjqwrjzkaf">
              <PlusIcon className="h-4 w-4" data-id="element-447" data-mp="34rd05jdczhda" /> Add employer
            </button>
          </div>

          <div className="mt-7 space-y-4" data-id="element-448" data-mp="39hjnzh01qxuh">
            {team.length === 0 ? <div className="rounded-2xl border border-dashed border-ink/20 bg-ivory-100 px-6 py-10 text-center" data-id="element-449" data-mp="39jo2stzhhh9j">
                <UsersRoundIcon className="mx-auto h-6 w-6 text-gold-dark" data-id="element-450" data-mp="2h18xbe7zvlnc" />
                <p className="mt-3 font-serif text-lg text-ink" data-id="element-451" data-mp="2gz5xy3kycg5y">Your employer list is empty</p>
                <p className="mt-1 text-sm text-ink-500" data-id="element-452" data-mp="3e1h3tx9shmna">Add colleagues who should be part of searches.</p>
              </div> : team.map((member, index) => <fieldset key={member.id} className="rounded-2xl border border-ink/10 bg-ivory-100 p-5" data-id="element-453" data-mp="3n0vr6nunth3k" data-mpi={index}>
                  <legend className="sr-only" data-id="element-454" data-mp="3rx30mqm9laq9">Employer {index + 1}</legend>
                  <div className="flex items-center justify-between gap-4" data-id="element-455" data-mp="2uczzxynv0he6">
                    <p className="text-xs font-medium uppercase tracking-widest2 text-gold-dark" data-id="element-456" data-mp="2z9qbq9smsqts">
                      Employer {String(index + 1).padStart(2, '0')}
                    </p>
                    <button type="button" onClick={() => removeMember(member.id)} className="inline-flex items-center gap-1.5 text-xs text-ink-500 transition-colors hover:text-red-700" aria-label={`Remove ${member.name || 'employer'}`} data-id="element-457" data-mp="390tc19dql1s0">
                      <Trash2Icon className="h-3.5 w-3.5" data-id="element-458" data-mp="28zpy1tezvrx7" /> Remove
                    </button>
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-459" data-mp="23zk60c4tc3el">
                    <TextField label="Name" value={member.name} onChange={value => updateMember(member.id, 'name', value)} compact data-id="element-460" data-mp="1z4ejpc7i0mpz" />
                    <TextField label="Title" value={member.title} onChange={value => updateMember(member.id, 'title', value)} compact data-id="element-461" data-mp="1ladfi2xs27i8" />
                    <TextField label="Department" value={member.department} onChange={value => updateMember(member.id, 'department', value)} compact data-id="element-462" data-mp="1bek7mwv0726s" />
                    <TextField label="Work email" value={member.email} onChange={value => updateMember(member.id, 'email', value)} type="email" compact data-id="element-463" data-mp="rqo14y6fntus" />
                  </div>
                </fieldset>)}
          </div>
        </section>

        <div className="flex flex-col-reverse items-start justify-between gap-4 border-t border-ink/10 pt-6 sm:flex-row sm:items-center" data-id="element-464" data-mp="1beofne414qi1">
          <p className="text-xs leading-relaxed text-ink-500" data-id="element-465" data-mp="1ffqh81lf5en6">
            Changes are saved in this browser for this workspace.
          </p>
          <Button type="submit" variant="gold" size="lg" data-id="element-466" data-mp="16nycm7j2szqi">
            <SaveIcon className="h-4 w-4" data-id="element-467" data-mp="16mdhogsunes4" /> Save firm details
          </Button>
        </div>
      </form>
    </div>;
}
function TextField({
  label,
  value,
  onChange,
  type = 'text',
  required,
  prefix,
  compact = false,
  inputMode
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  prefix?: React.ReactNode;
  compact?: boolean;
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
}) {
  return <label className="block" data-id="element-468" data-mp="2i8m9rauk51z5">
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-ink-600" data-id="element-469" data-mp="2iaymn9lgkm5c">
        {label}
      </span>
      <span className="relative block" data-id="element-470" data-mp="1pk6zf1mdf7u5">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" data-id="element-471" data-mp="1pkqfsdwdho62">{prefix}</span>}
        <input type={type} required={required} value={value} onChange={event => onChange(event.target.value)} inputMode={inputMode} className={`w-full rounded-xl border border-ink/15 bg-ivory-100 text-sm text-ink placeholder:text-ink-400 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold ${prefix ? 'pl-10 pr-4' : 'px-4'} ${compact ? 'py-2.5' : 'py-3'}`} data-id="element-472" data-mp="1lkg18cuilpfl" />
      </span>
    </label>;
}
function SelectField({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
}) {
  return <label className="block" data-id="element-473" data-mp="1peppss2weqgi">
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-ink-600" data-id="element-474" data-mp="1pehbcvr4nd37">
        {label}
      </span>
      <select value={value} onChange={event => onChange(event.target.value)} className="w-full rounded-xl border border-ink/15 bg-ivory-100 px-4 py-3 text-sm text-ink focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold" data-id="element-475" data-mp="1tf1b87334ut1">
        {options.map(option => <option key={option} data-id="element-476" data-mp="2d5dakdxwxakh">{option}</option>)}
      </select>
    </label>;
}
function readStored<T>(key: string, fallback: T): T {
  try {
    const stored = window.localStorage.getItem(key);
    return stored ? JSON.parse(stored) as T : fallback;
  } catch {
    return fallback;
  }
}
function createId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}